/**
 * Read Helcim's answer out of the URL after the card form's round trip.
 *
 * ⚠️ HELCIM SAYS "APPROVAL", NOT "APPROVED".
 *
 * This read used to accept only ``APPROVED``. A real card went through on
 * 2026-09-16, Helcim stored it and came back with
 * ``?response=1&responseMessage=APPROVAL&noticeMessage=New+Card+Stored...``,
 * and this returned ``approved: false`` — so the attach never ran, the
 * subscription stayed ``pending``, and the customer was told nothing at all. The
 * patient-payment flow in TreatmentPlans.js has always accepted both spellings,
 * which is why that path has been taking money for years while this one had
 * never completed once.
 *
 * Success is now anything beginning with APPROV, or Helcim's numeric
 * ``response=1``. Both are checked because the two are sent together and either
 * alone is enough.
 *
 * The app is HASH-routed (``createHashHistory``), so where the parameters land
 * depends on the return URL configured in the merchant account:
 *
 *   https://app.smilepass.com/?responseMessage=APPROVAL&cardToken=…#/page → location.search
 *   https://app.smilepass.com/#/page?responseMessage=APPROVAL&cardToken=…  → inside the hash
 *
 * Both are read. The live return lands in the query string, at the site root.
 */

// Everything Helcim.js appends, so the URL can be swept clean afterwards.
const HELCIM_PARAMS = [
  "response",
  "responseMessage",
  "noticeMessage",
  "cardToken",
  "cardNumber",
  "cardType",
  "cardExpiry",
  "transactionId",
  "date",
  "time",
  "type",
  "amount",
  "xml",
];

function hashQuery() {
  const hash = window.location.hash || "";
  const at = hash.indexOf("?");
  return at === -1 ? "" : hash.slice(at + 1);
}

/** True for APPROVED, APPROVAL, or the numeric response flag. */
function isApproval(responseMessage, response) {
  if (responseMessage && /^approv/i.test(String(responseMessage).trim())) return true;
  return String(response || "").trim() === "1";
}

/**
 * @returns {{
 *   present: boolean,    Helcim put something in the URL at all
 *   approved: boolean,
 *   cardToken: string|null,
 *   responseMessage: string|null,
 *   noticeMessage: string|null,
 *   from: "search"|"hash"|null,
 * }}
 */
export function readHelcimReturn() {
  const sources = [
    ["search", new URLSearchParams(window.location.search)],
    ["hash", new URLSearchParams(hashQuery())],
  ];
  for (const [from, params] of sources) {
    const responseMessage = params.get("responseMessage");
    const response = params.get("response");
    const cardToken = params.get("cardToken");
    if (responseMessage || cardToken || response) {
      return {
        present: true,
        approved: isApproval(responseMessage, response),
        cardToken: cardToken || null,
        responseMessage: responseMessage || null,
        noticeMessage: params.get("noticeMessage") || null,
        from,
      };
    }
  }
  return {
    present: false,
    approved: false,
    cardToken: null,
    responseMessage: null,
    noticeMessage: null,
    from: null,
  };
}

/**
 * Strip Helcim's parameters so a refresh can't re-run the attach and nothing
 * is left sitting in the address bar.
 *
 * Helcim.js appends far more than the fields we read — after the first live
 * charge the bar still showed cardHolderName, avsResponse, cvvResponse,
 * approvalCode, orderNumber, customerCode… — so at the site root the WHOLE
 * query string goes. That is safe: this only ever runs for a subscription
 * return (the handler is gated on `pendingSubscribe`), the app is hash-routed,
 * and nothing it needs lives in the root query string. Inside the hash the
 * route itself lives there, so only known keys are removed.
 */
export function clearHelcimReturn(from) {
  try {
    const url = new URL(window.location.href);
    if (from === "hash") {
      const hash = url.hash || "";
      const at = hash.indexOf("?");
      if (at !== -1) {
        const params = new URLSearchParams(hash.slice(at + 1));
        HELCIM_PARAMS.forEach((k) => params.delete(k));
        const rest = params.toString();
        url.hash = hash.slice(0, at) + (rest ? `?${rest}` : "");
      }
    } else {
      url.search = "";
    }
    window.history.replaceState({}, "", url.toString());
  } catch (e) {
    // A URL we can't parse is not worth breaking the success path over.
  }
}


/**
 * The card round trip can be read by more than one mounted component (the
 * app-wide hook and the onboarding purchase screen both look at the same URL).
 * Attaching twice is harmless — it is an update_or_create — but it makes two
 * calls and two toasts, and a refresh would do it again. One token, one attach.
 */
const DONE_KEY = "helcimAttachDone";

/**
 * ⚠️ THE MARKER IS PER PURCHASE, NOT PER CARD.
 *
 * It used to be the card token alone, and that silently killed a second
 * purchase. Helcim returns the SAME cardToken for a returning customer whose
 * card it already holds, so on 2026-09-16 a second checkout came back with the
 * token from the first one, this said "already handled", the attach never
 * fired, and the page reloaded to an unchanged screen with no error anywhere.
 *
 * Scoping it to the purchase in flight fixes that: a refresh of the SAME
 * purchase is still deduplicated, while a NEW purchase with the same card is
 * not. `scope` is the pendingSubscribe payload, which carries the office and
 * tier and is rewritten every time a checkout starts.
 */
function doneKeyFor(token, scope) {
  return [token || "", scope || ""].join("|");
}

export function helcimAttachAlreadyDone(token, scope) {
  try {
    return !!token && window.sessionStorage.getItem(DONE_KEY) === doneKeyFor(token, scope);
  } catch (e) {
    return false;
  }
}

export function markHelcimAttachDone(token, scope) {
  try {
    window.sessionStorage.setItem(DONE_KEY, doneKeyFor(token, scope));
  } catch (e) {
    // A browser refusing session storage just means a refresh may retry. The
    // attach is idempotent, so that is survivable.
  }
}

/** Starting a checkout clears any marker from a previous one. Belt and braces
 *  alongside the scoping above, so a stale marker can never mute a purchase. */
export function clearHelcimAttachMarker() {
  try {
    window.sessionStorage.removeItem(DONE_KEY);
  } catch (e) {
    /* nothing to clear */
  }
}
